/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * AddExportModal.tsx
 */

import React from "react";
import Modal, {ModalProps, ModalState} from "./Modal";
import FormError from "../form/FormError";
import {FormElement} from "../form/FormElement";
import {AbstractForm, FormListener} from "../../model/form/AbstractForm";
import {Field} from "../../model/form/Field";
import {FormException} from "../../model/form/FormException";
import HttpError from "../../utils/HttpError";
import {AbstractDTO} from "../../model/dto/AbstractDTO";

interface Props<DTO extends AbstractDTO, Form extends AbstractForm<DTO>> extends ModalProps {
    onSaveClicked: (dto: Map<string, any> | FormData) => void;
    emptyForm: Form
}

interface State<DTO extends AbstractDTO, Form extends AbstractForm<DTO>> extends ModalState {
    invalidField?: Field<any>;
    form: Form
}

export default class FormModal<DTO extends AbstractDTO, Form extends AbstractForm<DTO>> extends Modal<Props<DTO, Form>, State<DTO, Form>> implements FormListener<Form, DTO> {
    public state: State<DTO, Form>;
    protected _positiveButtonText = this.t.save;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: Props<DTO, Form>, state: State<DTO, Form>) {
        super(props, state);

        this.state = {
            form: props.emptyForm,
            loading: false,
            invalidField: undefined
        };
    }

    /**
     * componentDidMount
     */
    componentDidMount() {
        super.componentDidMount();
        this.state.form.addListener(this);
    }

    /**
     * componentWillUnmount
     */
    componentWillUnmount() {
        this.state.form.removeListener(this);
        super.componentWillUnmount();
    }

    /**
     * onFormUpdate
     * @param form
     */
    onFormUpdate = (form: AbstractForm<DTO>) => {
        this.forceUpdate();
    };


    /**
     * _setError
     * @private
     */
    private _setError = (invalidField?: Field<any>) => {
        this.setState({
            invalidField: invalidField
        });
    }

    /**
     * onSave
     */
    public onSave = async () => {
        const {form} = this.state;

        await this.setState({loading: true, invalidField: undefined});

        try {
            this.props.onSaveClicked(form.compile());
        } catch (e) {
            await this._setError((e instanceof FormException) ? e.field : e.toString());
        } finally {
            await this.setState({loading: false});
        }
    };

    /**
     * _applyInputChange
     * @param key
     * @param value
     * @private
     */
    private _applyInputChange = (key: string, value: any) => {
        let form = this.state.form;

        form.fields = form.applyValue(key, value)

        this.setState({form: form});
    }

    /**
     * renderBody
     */
    public renderBody(): JSX.Element {
        const {form, invalidField} = this.state;
        const {statusCode} = form;

        if ((statusCode && statusCode === 200) || (statusCode && statusCode === 201)) {
            this.dismiss()
        }

        return <> {
            [...form.fields.values()].map(field => {
                return <div key={`${field.name}-div`}>
                    <FormElement key={field.name} field={field} onChange={this._applyInputChange}/>

                    {invalidField?.name === field.name && <FormError key={`${field.name}-error`} field={invalidField}/>}
                </div>
            })}
            {statusCode && <HttpError statusCode={statusCode}/>}
        </>;
    }
}